import React, { useState } from "react";
import { Mail, Loader2, CheckCircle2, AlertCircle, Send } from "lucide-react";
import { API } from "../products";
import { useLang } from "../i18n";
import { track } from "../analytics";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// variant: "light" (dark bg, for footer) | "section" (light bg, for homepage)
export const SubscribeForm = ({ variant = "section" }) => {
  const { t } = useLang();
  const s = t.subscribe;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setStatus("error");
      setMsg(s.invalidEmail);
      return;
    }
    setStatus("loading");
    setMsg("");
    try {
      const res = await fetch(`${API}/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || s.error);
      setStatus("success");
      setMsg(s.success);
      setName("");
      setEmail("");
      track("newsletter_subscribe", { label: variant });
    } catch (err) {
      setStatus("error");
      setMsg(typeof err.message === "string" ? err.message : s.error);
    }
  };

  const dark = variant === "light";
  const inputCls = dark
    ? "w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500"
    : "w-full px-4 py-2.5 rounded-lg bg-white border border-slate-300 text-slate-800 placeholder-slate-400 outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500";

  return (
    <form onSubmit={submit} data-testid={`subscribe-form-${variant}`} className="w-full">
      <div className="flex flex-col sm:flex-row gap-2.5">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={s.name}
          aria-label={s.name}
          className={`${inputCls} sm:flex-1`}
          data-testid="subscribe-name"
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={s.email}
          aria-label={s.email}
          required
          className={`${inputCls} sm:flex-1`}
          data-testid="subscribe-email"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-bold transition-colors disabled:opacity-60 shrink-0"
          data-testid="subscribe-submit"
        >
          {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {status === "loading" ? s.sending : s.button}
        </button>
      </div>
      {status === "success" && (
        <p className={`mt-3 text-sm font-semibold inline-flex items-center gap-1.5 ${dark ? "text-green-400" : "text-green-700"}`} data-testid="subscribe-success">
          <CheckCircle2 className="w-4 h-4" /> {msg}
        </p>
      )}
      {status === "error" && (
        <p className="mt-3 text-sm font-semibold inline-flex items-center gap-1.5 text-red-500" data-testid="subscribe-error">
          <AlertCircle className="w-4 h-4" /> {msg}
        </p>
      )}
      {status !== "success" && (
        <p className={`mt-2.5 text-xs ${dark ? "text-slate-500" : "text-slate-400"}`}>{s.privacyNote}</p>
      )}
    </form>
  );
};

// Full homepage section wrapper
export const SubscribeSection = () => {
  const { t } = useLang();
  const s = t.subscribe;
  return (
    <section id="subscribe" className="py-16 lg:py-20 bg-slate-900" data-testid="home-subscribe">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <span className="inline-flex items-center gap-2 text-green-500 font-bold uppercase tracking-widest text-sm">
          <Mail className="w-4 h-4" /> {s.eyebrow}
        </span>
        <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-white">{s.title}</h2>
        <p className="mt-3 text-slate-400">{s.sub}</p>
        <div className="mt-7 text-left">
          <SubscribeForm variant="light" />
        </div>
      </div>
    </section>
  );
};
